import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Button } from '@mui/material';
import { Title } from 'shared/components/abstract/interactables';
import FormCard from 'shared/components/FormCard';
import {
  addEmployeeToGroups, createUser, getRoles, searchUserGroups,
} from 'shared/api/admin';
import { useAuth, useRequest } from 'shared/hooks';
import { VARIANTS } from 'shared/constants';
import { TextEditor } from 'shared/components/data-display/editors';
import {
  AsyncMultipleSelect,
  AsyncSearchableSelect,
} from 'shared/components/select/SearchableSelects';
import { userGroupEntity } from 'shared/entityDetails';
import { getUserPath } from 'shared/routes/admin';

export default function UserCreator() {
  const { request } = useRequest();
  const { user: currentLoggedInUser } = useAuth();
  const [valid, setValid] = useState(false);
  const [stagedAddGroups, setStagedAddGroups] = useState([]);
  const [user, setUser] = useState({
    username: '',
    fullname: '',
    role: undefined,
    email: '',
  });

  const {
    username,
    fullname,
    role,
    email,
  } = user;

  useEffect(() => {
    setValid(!!username && !!fullname && !!role && !!email);
  }, [email, fullname, role, username]);

  const setValue = (name) => (value) => {
    if (name === 'email') {
      setUser({
        ...user,
        email: value,
        username: value?.split('@')[0],
      });
    } else if (name === 'role') {
      setUser({
        ...user,
        role: value,
      });
    } else {
      setUser({
        ...user,
        [name]: value,
      });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const result = await request(createUser({
      ...user,
      roleId: user.role.id,
    }), 'Created user');
    if (result) {
      if (stagedAddGroups.length > 0) {
        const addGroupsIds = stagedAddGroups.map((group) => group.id);
        const addToGroupsResult = await request(addEmployeeToGroups({
          group_ids: addGroupsIds,
          employee_id: parseInt(result.id, 10),
          created_by_id: currentLoggedInUser.id,
        }), 'Added employee to user groups');
        if (addToGroupsResult) {
          setStagedAddGroups([]);
        }
      }
      setUser(result);
    }
  };

  if (user.id) return <Redirect to={getUserPath(user.id)} />;

  return (
    <form onSubmit={onSubmit}>
      <Title title="Create User" />
      <FormCard>
        <Grid
          container
          size={{
            xs: 12,
            lg: 6,
          }}
          spacing={1}
        >
          <Grid
            size={{ lg: 5, xs: 12 }}
            sx={{ display: 'flex', flexDirection: 'column', rowGap: 1 }}
          >
            <TextEditor setValue={setValue('fullname')} label="Full Name" value={fullname} required />
            <TextEditor setValue={setValue('email')} label="Email" value={email} required />
            <TextEditor setValue={setValue('username')} label="Username" value={username} required />
            <AsyncSearchableSelect
              label="Role"
              onChange={setValue('role')}
              searchRequest={getRoles}
              getOptionValue={(r) => r.id}
              getOptionLabel={(r) => r.name}
              selectedOption={role}
            />
            <AsyncMultipleSelect
              searchRequest={searchUserGroups}
              getOptionLabel={userGroupEntity.getOptionLabel}
              getOptionValue={userGroupEntity.getOptionValue}
              onChange={setStagedAddGroups}
              label="Add Group"
              selectedOptions={stagedAddGroups}
            />
            <Button
              type="submit"
              variant={VARIANTS.contained}
              color={VARIANTS.primary}
              disabled={!valid}
              sx={{ alignSelf: 'flex-end' }}
            >
              Create User
            </Button>
          </Grid>
        </Grid>
      </FormCard>
    </form>
  );
}
